// A tiny paged-list helper (plan item 2): the offset + limit + has_more +
// "Load more" pattern proven in history.ts, packaged so every unbounded list
// behaves identically. Rows append on "Load more"; reload() restarts from
// offset 0 (for a search-query change). Rows go into a caller-supplied container
// — a <div> for stacked rows or a <tbody> for a table — so both share one
// behavior, one place to fix.

import { ApiError } from "../api.ts";
import { h } from "../dom.ts";
import { t } from "../i18n/index.ts";

/** One page from the server: the rows plus whether more exist (history's trick
 *  of fetching limit+1 to learn `has_more` without a COUNT). */
export interface Page<T> {
  items: T[];
  has_more: boolean;
}

export interface PagedListOpts<T> {
  /** Fetch one page of `limit` items starting at `offset`. */
  fetchPage: (offset: number, limit: number) => Promise<Page<T>>;
  /** Build one row element from an item. */
  renderRow: (item: T) => HTMLElement;
  /** Rows per page (default 30, matching History). */
  pageSize?: number;
  /** Container rows are appended to. Defaults to a fresh <div class="paged-rows">.
   *  Pass a <tbody> to render into a table. */
  rowsContainer?: HTMLElement;
  /** Element placed in the root before the status/button. Defaults to
   *  `rowsContainer`; pass the <table> when `rowsContainer` is its <tbody>. */
  rowsHost?: HTMLElement;
  /** Shown when the first page comes back empty. A function is resolved at load
   *  time, so it can reflect the current search query. */
  emptyText?: string | (() => string);
  /** Shown while the first page loads (default "Loading…"). */
  loadingText?: string;
  /** Error text when the first page fails (falls back to the ApiError message). */
  errorText?: string;
  /** "Load more" button label (default History's). */
  loadMoreLabel?: string;
  /** Called after each page renders — (items, append). Lets the caller seed
   *  "recently played" chips from the first page, refresh presence dots, … */
  onPage?: (items: T[], append: boolean) => void;
}

export interface PagedList {
  /** Root element to insert into the DOM. */
  element: HTMLElement;
  /** The container rows live in (for keyed lookups by the caller). */
  rows: HTMLElement;
  /** Reload from offset 0 (e.g. the search query changed). */
  reload: () => Promise<void>;
}

export function pagedList<T>(opts: PagedListOpts<T>): PagedList {
  const pageSize = opts.pageSize ?? 30;
  const rows = opts.rowsContainer ?? h(`<div class="paged-rows"></div>`);
  const host = opts.rowsHost ?? rows;

  const root = h(`
    <div class="paged">
      <p class="paged-msg fine" role="status" hidden></p>
      <div class="load-more-wrap">
        <button class="btn load-more" type="button" hidden>${opts.loadMoreLabel ?? t("history.loadMore")}</button>
      </div>
    </div>`);
  const msg = root.querySelector<HTMLElement>(".paged-msg")!;
  const moreBtn = root.querySelector<HTMLButtonElement>(".load-more")!;
  // The rows host (a <div> or a <table>) sits above the status line + button.
  root.insertBefore(host, msg);

  let offset = 0;
  let loading = false;

  const setMsg = (text: string) => {
    msg.textContent = text;
    msg.hidden = !text;
  };

  const load = async (append: boolean): Promise<void> => {
    if (loading) return;
    loading = true;
    moreBtn.disabled = true;
    if (!append) {
      offset = 0;
      rows.replaceChildren();
      moreBtn.hidden = true;
      setMsg(opts.loadingText ?? t("common.loading"));
    }
    try {
      const { items, has_more } = await opts.fetchPage(offset, pageSize);
      if (offset === 0 && items.length === 0) {
        setMsg(typeof opts.emptyText === "function" ? opts.emptyText() : (opts.emptyText ?? ""));
      } else {
        setMsg(""); // clears a previous append-failure notice too
        for (const it of items) rows.append(opts.renderRow(it));
      }
      offset += items.length;
      moreBtn.hidden = !has_more;
      opts.onPage?.(items, append);
    } catch (err) {
      // On an append failure keep the rows and the button so the user can
      // retry — but always say what happened (plan A6).
      if (!append) rows.replaceChildren();
      setMsg(err instanceof ApiError ? err.message : (opts.errorText ?? t("common.loadFailed")));
    } finally {
      loading = false;
      moreBtn.disabled = false;
    }
  };

  moreBtn.addEventListener("click", () => void load(true));

  // Kick off the first page; the caller inserts `element` and it fills in.
  void load(false);

  return { element: root, rows, reload: () => load(false) };
}
